import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Users } from "lucide-react";
import PhotoGallery from "@/components/PhotoGallery";
import CustomerContracts from "@/components/CustomerContracts";

const empty = { name: "", social_security: "", driver_license: "", phone: "", email: "", address: "", language: "pt", notes: "" };

export default function Clientes() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/customers");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) =>
    [i.name, i.social_security, i.driver_license, i.phone, i.email]
      .some((x) => (x || "").toLowerCase().includes(q.toLowerCase()))
  );

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name,
      social_security: it.social_security || "",
      driver_license: it.driver_license || "",
      phone: it.phone, email: it.email || "",
      address: it.address || "", language: it.language || "pt", notes: it.notes || ""
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, email: form.email || null };
      if (editing) { await api.put(`/customers/${editing.id}`, payload); toast.success(t("customers.updated")); }
      else { await api.post("/customers", payload); toast.success(t("customers.created")); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("customers.confirmDelete"))) return;
    try { await api.delete(`/customers/${id}`); toast.success(t("customers.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="overline">{t("customers.pageTitle", { n: items.length })}</div>
          <p className="text-sm text-zinc-500 mt-1">{t("customers.pageSubtitle")}</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none w-64" data-testid="clientes-search" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="add-customer-button">
                <Plus className="w-4 h-4 mr-2" /> {t("customers.newCustomer")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl rounded-none max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? t("customers.editCustomer") : t("customers.registerCustomer")}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <Field label={t("customers.fullName")} full><Input className="rounded-none" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" /></Field>
                <Field label={t("customers.ssn")}><Input className="rounded-none" value={form.social_security} onChange={(e) => setForm({ ...form, social_security: e.target.value })} placeholder="XXX-XX-XXXX" data-testid="customer-ssn-input" /></Field>
                <Field label={t("customers.license")}><Input className="rounded-none" value={form.driver_license} onChange={(e) => setForm({ ...form, driver_license: e.target.value })} data-testid="customer-license-input" /></Field>
                <Field label={t("customers.phone")}><Input className="rounded-none" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                <Field label={t("customers.email")}><Input type="email" className="rounded-none" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label={t("customers.preferredLanguage")} full>
                  <select
                    value={form.language || "pt"}
                    onChange={(e) => setForm({ ...form, language: e.target.value })}
                    className="border border-input bg-background h-10 px-3 text-sm w-full rounded-none focus:outline-none focus:ring-1 focus:ring-[#F5A623]"
                    data-testid="customer-language-select"
                  >
                    <option value="pt">🇧🇷 Português</option>
                    <option value="en">🇺🇸 English</option>
                    <option value="es">🇪🇸 Español</option>
                  </select>
                  <p className="text-[10px] text-zinc-500 mt-1">{t("customers.preferredLanguageHint")}</p>
                </Field>
                <Field label={t("customers.address")} full><Input className="rounded-none" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                <Field label={t("common.notes")} full><Input className="rounded-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                {editing && (
                  <div className="col-span-2 mt-2 space-y-4">
                    <CustomerContracts customerId={editing.id} />
                    <PhotoGallery
                      ownerType="customer"
                      ownerId={editing.id}
                      accept="image/*,application/pdf"
                      title={t("photos.customerDocs")}
                      description={t("photos.customerDocsHint")}
                      gridCols={3}
                    />
                  </div>
                )}
                <DialogFooter className="col-span-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">{t("common.cancel")}</Button>
                  <Button type="submit" className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="save-customer-button">{t("common.save")}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-zinc-200 p-16 text-center">
          <Users className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <div className="overline mb-2">{t("customers.emptyTitle")}</div>
          <p className="text-sm text-zinc-500 mb-6">{t("customers.emptySub")}</p>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>{t("customers.fullName")}</Th><Th>{t("customers.ssn")}</Th><Th>{t("customers.license")}</Th><Th>{t("customers.phone")}</Th><Th>{t("customers.email")}</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`customer-row-${c.social_security}`}>
                  <Td className="font-medium">{c.name}</Td>
                  <Td className="font-mono text-xs">{c.social_security}</Td>
                  <Td className="font-mono text-xs">{c.driver_license}</Td>
                  <Td className="font-mono text-xs">{c.phone}</Td>
                  <Td className="text-xs">{c.email || "—"}</Td>
                  <Td>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => openEdit(c)} className="p-1.5 border border-zinc-200 hover:border-zinc-400"><Edit2 className="w-3 h-3" /></button>
                      <button onClick={() => remove(c.id)} className="p-1.5 border border-zinc-200 hover:border-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 overline">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Field = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <label className="overline">{label}</label>{children}
  </div>
);
